<template>
  <div class="card-reset" :class="id">
    <div v-html="getDivStyles()"></div>
    <div ref="cardContainer" class="card-container"></div>
  </div>
</template>

<script lang="ts" setup>
import { renderElements, getStyles, addLoading, replacePanel } from '@netcall/card-renderer';
import { nanoid } from 'nanoid';
import { Card } from './card';
import { CardSubmitEvent } from './card-submit-event';
import { onMounted, onBeforeUnmount, PropType, ref, watch, toRef } from 'vue';

defineOptions({
  name: 'n-app',
});

const props = defineProps({
  readonly: {
    type: Boolean,
    required: true,
  },
  card: {
    type: Object as PropType<Card>,
    required: true,
  },
  colour: {
    type: String,
    required: true,
  },
  location: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'submit', cardEvent: CardSubmitEvent): void;
}>();

const id = `nc_${nanoid()}`;

const cardContainer = ref<HTMLElement | null>(null);
const submitTimeout = ref<ReturnType<typeof setTimeout>>();
const readonly = toRef(props.readonly);

watch(
  () => props.card,
  (card: Card) => renderCard(card),
);

watch(
  () => props.readonly,
  (value: boolean) => {
    readonly.value = value;
    renderCard(props.card);
  },
);

onMounted(() => {
  renderCard(props.card);
  document.addEventListener('fresco-panel-submit', cardSubmitHandler);
});

onBeforeUnmount(() => {
  document.removeEventListener('fresco-panel-submit', cardSubmitHandler);
});

function getDivStyles() {
  return getStyles(props.colour, id).innerHTML;
}

function renderCard(card: Card) {
  if (!cardContainer.value) {
    return;
  }
  clearSubmitTimeout();
  cardContainer.value.innerHTML = '';
  if (isCardComponentsValid(card)) {
    const cardJson = JSON.stringify(card.content.components);
    renderElements(card.id || '', cardJson, cardContainer.value, props.location, props.readonly);
  } else {
    renderError(card);
  }
}

function renderError(card: Card) {
  renderElements(card.id || '', '{}', cardContainer.value, props.location, props.readonly);
}

function isCardComponentsValid(card: Card) {
  return card.content && card.content.components && card.content.components.length > 0;
}

function cardSubmitHandler(e: Event) {
  if (isEventForCard(e)) {
    const customEvent = e as CustomEvent;
    const { panelId } = customEvent.detail;
    addLoadingSpinner(panelId);
    onSubmit(customEvent.detail);
  }
}

function isEventForCard(evt: any) {
  return evt.detail.panelId === props.card.id;
}

function onSubmit(e: CardSubmitEvent) {
  emit('submit', e);
}

function addLoadingSpinner(panelId: string) {
  addLoading(panelId);
  setSubmitTimeout(panelId);
}

function setSubmitTimeout(panelId: string) {
  submitTimeout.value = setTimeout(() => {
    replaceWithError(panelId);
  }, 30000);
}

function clearSubmitTimeout() {
  if (submitTimeout.value) {
    clearTimeout(submitTimeout.value);
  }
}

function replaceWithError(panelId: string) {
  // No method in render engine to display error state, but passing invalid json will result in that
  replacePanel(panelId, '!!!');
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
@use '@netcall/card-renderer/dist/styles' as *;
.card-reset {
  .label {
    font-size: unset;
  }

  input,
  textarea {
    font-size: unset;
    height: unset;
    box-shadow: unset;
  }

  ul {
    list-style: unset;
  }

  ul,
  ol {
    padding-left: 40px;
    margin-block-start: 1em;
    margin-block-end: 1em;
  }

  a {
    color: #0000ee;
    text-decoration: underline;
  }

  .image {
    // fixes spacing issue for elements after the image
    display: unset;
    position: unset;
  }

  strong {
    color: unset;
  }
}
</style>
